import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, View } from 'react-native'

import { Dapp } from '@ambire-common/interfaces/dapp'
import CloseIcon from '@common/assets/svg/CloseIcon'
import DAppsIcon from '@common/assets/svg/DAppsIcon'
import ErrorFilledIcon from '@common/assets/svg/ErrorFilledIcon'
import ManifestFallbackIcon from '@common/assets/svg/ManifestFallbackIcon'
import PowerIcon from '@common/assets/svg/PowerIcon'
import StarIcon from '@common/assets/svg/StarIcon'
import UpArrowIcon from '@common/assets/svg/UpArrowIcon'
import Button from '@common/components/Button'
import { createGlobalTooltipDataSet } from '@common/components/GlobalTooltip'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings, { SPACING_TY } from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import ManifestImage from '@web/components/ManifestImage'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'

import getStyles from './styles'

type Props = {
  dapp: Dapp | null
  isHovered?: boolean
  inModal?: boolean
  isCurrentDapp?: boolean
  openBottomSheet: (dest?: 'top' | 'default' | undefined) => void
  closeBottomSheet: (dest?: 'default' | 'alwaysOpen' | undefined) => void
}

const DappControl = ({
  dapp,
  inModal,
  isHovered,
  isCurrentDapp,
  openBottomSheet,
  closeBottomSheet
}: Props) => {
  const { styles, theme } = useTheme(getStyles)
  const { dispatch } = useBackgroundService()
  const { t } = useTranslation()
  const { networks } = useNetworksControllerState()

  const network = useMemo(
    () =>
      networks.filter((n) => Number(n.chainId) === dapp?.chainId)[0] ||
      networks.filter((n) => n.chainId === 1n)[0],
    [dapp?.chainId, networks]
  )

  const showDisconnectButton = !!dapp?.isConnected && (isHovered || inModal)

  const dappInitials = useMemo(() => {
    const fullName = dapp?.name || ''

    if (!fullName) return null

    const words = fullName.split(' ').filter((word) => word.length > 0)
    const firstSymbol = words?.[0]?.[0]

    if (!firstSymbol) return null

    return firstSymbol.toUpperCase()
  }, [dapp?.name])

  const fallbackIcon = useCallback(() => {
    if (!dappInitials) return <ManifestFallbackIcon />

    return (
      <View style={styles.fallbackWrapper}>
        <Text color={theme.infoText}>{dappInitials}</Text>
      </View>
    )
  }, [dappInitials, styles.fallbackWrapper, theme.infoText])

  return (
    <View>
      <View
        style={[
          styles.titleWrapper,
          !inModal &&
            dapp?.blacklisted === 'BLACKLISTED' && { backgroundColor: theme.errorBackground }
        ]}
      >
        <Text weight="medium" fontSize={12} appearance="secondaryText">
          {isCurrentDapp ? t('Manage current app') : t('Manage app')}
        </Text>
      </View>

      <View style={styles.border}>
        {dapp ? (
          <View style={styles.currentDApp}>
            <View>
              {dapp.blacklisted === 'BLACKLISTED' && (
                <View
                  style={{ position: 'absolute', right: -3, top: -2, zIndex: 1 }}
                  dataSet={createGlobalTooltipDataSet({
                    id: 'blacklisted-app-tooltip',
                    content: t('Blacklisted app!'),
                    delayShow: 200,
                    border: `1px solid ${theme.errorDecorative as string}`,
                    style: {
                      fontSize: 12,
                      backgroundColor: theme.errorBackground as string,
                      padding: SPACING_TY,
                      color: theme.errorDecorative as string
                    }
                  })}
                >
                  <ErrorFilledIcon width={14} height={14} />
                </View>
              )}

              <ManifestImage uri={dapp.icon || ''} size={32} fallback={fallbackIcon} />
            </View>
            <View style={[spacings.mlMi, flexbox.flex1]}>
              <View style={[flexbox.directionRow, flexbox.flex1]}>
                <Text fontSize={12} weight="medium" numberOfLines={1} style={spacings.mrTy}>
                  {dapp.name}
                </Text>
                {!!dapp.isConnected && (
                  <Pressable
                    onPress={() => {
                      dispatch({
                        type: 'DAPP_CONTROLLER_UPDATE_DAPP',
                        params: { id: dapp.id, dapp: { favorite: !dapp.favorite } }
                      })
                    }}
                    style={[flexbox.alignSelfStart, spacings.mrLg]}
                  >
                    <StarIcon isFilled={dapp.favorite} />
                  </Pressable>
                )}
              </View>
              <Text
                weight="regular"
                color={dapp.isConnected ? theme.successText : theme.errorText}
                fontSize={10}
              >
                {dapp.isConnected
                  ? t('Connected on {{network}}', {
                      network: network?.name || String(dapp.chainId)
                    })
                  : t('Not connected')}
              </Text>
            </View>
          </View>
        ) : (
          <View style={styles.currentDApp}>
            <DAppsIcon width={24} height={24} color={theme.secondaryText} />
            <Text fontSize={14} appearance="secondaryText" weight="regular" style={spacings.mlTy}>
              {t('No app found')}
            </Text>
          </View>
        )}
        <View style={flexbox.directionRow}>
          {!!showDisconnectButton && !!dapp?.url && (
            <Button
              type="danger"
              size="small"
              hasBottomSpacing={false}
              text={t('Disconnect')}
              style={spacings.mrTy}
              onPress={() => {
                dispatch({
                  type: 'DAPPS_CONTROLLER_DISCONNECT_DAPP',
                  params: { id: dapp.id, url: dapp.url }
                })
              }}
            >
              <View style={spacings.plTy}>
                <PowerIcon />
              </View>
            </Button>
          )}
          {dapp?.isConnected && (
            <Button
              type="secondary"
              size="small"
              hasBottomSpacing={false}
              text={inModal ? t('Close') : t('Manage')}
              onPress={() => {
                !inModal && openBottomSheet()
                inModal && closeBottomSheet()
              }}
            >
              <View style={spacings.plTy}>
                {!inModal && <UpArrowIcon color={theme.primary} />}
                {!!inModal && <CloseIcon color={theme.primary} />}
              </View>
            </Button>
          )}
        </View>
      </View>
    </View>
  )
}

export default React.memo(DappControl)
